// Registry of "Show X" toggles for the floor toolbar. Each item owns its label,
// default state, and how to push that state to the scene (`apply`). The toolbar
// renders its buttons from list(); applyAll() pushes every item's state; a new
// toggle is ONE register() call instead of edits spread across main + floor-ui
// (the interface, getState, the handler, the button, and applyWallVisibility).

export interface VisibilityItem {
  id: string;
  label: string;
  defaultOn: boolean; // visible by default (app boot / loaded layout)
  newLayoutOn?: boolean; // visible on a fresh/blank layout; falls back to defaultOn
  apply: (on: boolean) => void; // push the on/off state to the scene
  onToggle?: (on: boolean) => void; // extra side effect when the USER toggles it
}

export interface VisibilityRegistry {
  register(item: VisibilityItem): void;
  isOn(id: string): boolean;
  toggle(id: string): void;
  applyAll(): void; // push every item's current state to the scene
  resetForNewLayout(): void; // set each to its new-layout default, then applyAll
  list(): { id: string; label: string; on: boolean }[]; // for the toolbar
}

export function createVisibilityRegistry(): VisibilityRegistry {
  interface Entry extends VisibilityItem {
    on: boolean;
  }
  const items: Entry[] = [];
  const find = (id: string): Entry | undefined => items.find((it) => it.id === id);
  const applyAll = (): void => {
    for (const it of items) it.apply(it.on);
  };
  return {
    register: (item): void => {
      items.push({ ...item, on: item.defaultOn });
    },
    isOn: (id): boolean => find(id)?.on ?? false,
    toggle: (id): void => {
      const it = find(id);
      if (!it) return;
      it.on = !it.on;
      it.apply(it.on);
      it.onToggle?.(it.on);
    },
    applyAll,
    resetForNewLayout: (): void => {
      for (const it of items) it.on = it.newLayoutOn ?? it.defaultOn;
      applyAll();
    },
    list: (): { id: string; label: string; on: boolean }[] =>
      items.map((it) => ({ id: it.id, label: it.label, on: it.on })),
  };
}
